import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Check,
  ArrowLeft,
  Edit,
  Eye,
  Layers3,
  MoreHorizontal,
  PackageCheck,
  PackagePlus,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import {
  createCommissionPackage,
  deleteCommissionRecord,
  createCommissionSlab,
  deleteCommissionPackage,
  deleteCommissionSlab,
  getBanks,
  getCommissionPackage,
  getCommissionRecord,
  getCommissionPackages,
  getCommissionRecords,
  updateCommissionRecord,
  updateCommissionPackage,
  updateCommissionSlab,
} from "../../lib/api";

const PACKAGE_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const USER_TYPE_OPTIONS = [
  { value: "agent", label: "Agent" },
  { value: "employee", label: "Employee" },
  { value: "super_admin", label: "Super Admin" },
  { value: "wl_admin", label: "Whitelabel Admin" },
];

const LOAN_TYPE_OPTIONS = [
  { value: "home", label: "Home Loan" },
  { value: "personal", label: "Personal Loan" },
  { value: "business", label: "Business Loan" },
  { value: "car", label: "Car Loan" },
  { value: "education", label: "Education Loan" },
  { value: "other", label: "Other" },
];

const DEFAULT_BANK_OPTIONS = [
  "All Banks",
  "HDFC Bank",
  "ICICI Bank",
  "SBI",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Bajaj Finance",
];

const CALCULATION_NAME_OPTIONS = [
  { value: "flat", label: "Flat" },
  { value: "percentage", label: "Percentage" },
];

const defaultPackageForm = {
  package_name: "",
  user_type: "agent",
  status: "active",
};

const defaultSlabForm = {
  loan_type: "",
  bank: "All Banks",
  calculation_name: "percentage",
  capping: "",
  admin_commission: "",
  employee_commission: "",
  min_amount: "",
  max_amount: "",
  commission_rate: "",
  status: "active",
};

function labelForOption(options, value) {
  return options.find((option) => option.value === value)?.label || value;
}

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function normalizePackage(item) {
  const slabs = Array.isArray(item.slabs)
    ? item.slabs.map((slab) => ({
      id: slab.id,
      bank: slab.bank_name || "All Banks",
      bank_id: slab.bank_id || null,
      loan_type: slab.loan_type,
      calculation_name: slab.calculation_name,
      capping: Number(slab.capping || 0),
      admin_commission: Number(slab.admin_commission || 0),
      employee_commission: Number(slab.employee_commission || 0),
      min_amount: Number(slab.min_amount || 0),
      max_amount: Number(slab.max_amount || 0),
      commission_rate: Number(slab.rate || 0),
      status: slab.status,
    }))
    : [];
  return {
    id: item.id,
    package_name: item.package_name,
    user_type: item.user_type,
    status: item.status,
    slab_count: Number(item.slab_count ?? slabs.length ?? 0),
    slabs,
  };
}

function normalizeCommissionRecord(item) {
  return {
    id: item.id,
    loanId: item.loan_application_id,
    agentName: item.recipient_name || labelForOption(USER_TYPE_OPTIONS, item.recipient_role),
    amount: Number(item.amount || 0),
    status: item.status,
    bank: item.bank_name || "—",
    requestDate: formatDateTime(item.created_at),
    processedDate: item.paid_at ? formatDateTime(item.paid_at) : null,
    recipientRole: item.recipient_role,
    packageName: item.package_name,
    loanType: item.loan_type,
    calculationName: item.calculation_name,
    disbursedAmount: Number(item.disbursed_amount || 0),
    appliedRate: Number(item.applied_rate || 0),
    capping: Number(item.capping || 0),
    createdAt: item.created_at,
    paidAt: item.paid_at,
    updatedAt: item.updated_at,
  };
}

function statusBadgeClass(status) {
  return status === "active"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-slate-100 text-slate-700 border-slate-200";
}

function PackageFormDialog({ open, mode, form, userTypeOptions, onFormChange, onClose, onSubmit }) {

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add Commission Package" : "Edit Commission Package"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Create package basic information first. Slabs can be managed after package creation."
              : "Update package details and manage slabs inside this package."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="package_name">Package Name</Label>
            <Input
              id="package_name"
              value={form.package_name}
              onChange={(event) =>
                onFormChange((current) => ({
                  ...current,
                  package_name: event.target.value,
                }))
              }
              placeholder="Enter package name"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-2">
              <Label>User Type</Label>
              <Select
                value={form.user_type}
                onValueChange={(value) =>
                  onFormChange((current) => ({ ...current, user_type: value }))
                }
              >
                <SelectTrigger>

                  <SelectValue placeholder="Select user type" />
                </SelectTrigger>
                <SelectContent>
                  {(userTypeOptions || USER_TYPE_OPTIONS).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(value) =>
                  onFormChange((current) => ({ ...current, status: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {PACKAGE_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button type="button" className="bg-emerald-600 hover:bg-emerald-700" onClick={onSubmit}>
            {mode === "create" ? "Create Package" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ open, title, description, onClose, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ title, description, actionLabel, onAction }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-6 py-12 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
        <Layers3 className="h-6 w-6 text-slate-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600">{description}</p>
      {actionLabel ? (
        <Button className="mt-5 bg-emerald-600 hover:bg-emerald-700" onClick={onAction}>
          <Plus className="mr-2 h-4 w-4" />
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

function validateSlabForm(form) {
  const errors = {};
  if (!form.loan_type) errors.loan_type = "Loan type is required.";
  if (!form.bank) errors.bank = "Bank is required.";
  if (!form.calculation_name) errors.calculation_name = "Calculation is required.";
  if (form.min_amount === "" || Number.isNaN(Number(form.min_amount))) {
    errors.min_amount = "Enter a valid minimum amount.";
  }
  if (form.max_amount === "" || Number.isNaN(Number(form.max_amount))) {
    errors.max_amount = "Enter a valid maximum amount.";
  }
  if (form.commission_rate === "" || Number.isNaN(Number(form.commission_rate))) {
    errors.commission_rate = "Enter a valid rate.";
  }
  if (form.capping === "" || Number.isNaN(Number(form.capping))) {
    errors.capping = "Enter a valid capping value.";
  }
  if (form.admin_commission === "" || Number.isNaN(Number(form.admin_commission))) {
    errors.admin_commission = "Enter a valid admin commission.";
  }
  if (form.employee_commission === "" || Number.isNaN(Number(form.employee_commission))) {
    errors.employee_commission = "Enter a valid employee commission.";
  }
  if (
    !errors.min_amount &&
    !errors.max_amount &&
    Number(form.min_amount) > Number(form.max_amount)
  ) {
    errors.max_amount = "Max amount must be greater than or equal to min amount.";
  }
  return errors;
}

function InlineError({ message }) {
  if (!message) return null;
  return <p className="mt-1 text-xs text-red-600">{message}</p>;
}

function RateInput({ value, onChange, calculationName, error }) {
  const isFlat = calculationName === "flat";
  return (
    <div>
      <div className="relative">
        {isFlat ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
            Rs
          </span>
        ) : null}
        <Input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={onChange}
          placeholder="Rate"
          className={isFlat ? "pl-9 pr-3" : "pl-3 pr-9"}
        />
        {!isFlat ? (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
            %
          </span>
        ) : null}
      </div>
      <InlineError message={error} />
    </div>
  );
}

function InlineSlabTable({
  slabs,
  bankOptions,
  editingSlabId,
  slabForm,
  slabErrors,
  onFieldChange,
  onAddNew,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onToggleStatus,
}) {
  const renderEditableRow = (rowKey) => (
    <TableRow key={rowKey} className="bg-emerald-50/40 align-top">
      <TableCell className="min-w-[170px]">
        <Select value={slabForm.bank} onValueChange={(value) => onFieldChange("bank", value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select bank" />
          </SelectTrigger>
          <SelectContent>
            {(bankOptions || DEFAULT_BANK_OPTIONS).map((bank) => (
              <SelectItem key={bank} value={bank}>
                {bank}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <InlineError message={slabErrors.bank} />
      </TableCell>
      <TableCell className="min-w-[180px]">
        <Select value={slabForm.loan_type} onValueChange={(value) => onFieldChange("loan_type", value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {LOAN_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <InlineError message={slabErrors.loan_type} />
      </TableCell>

      <TableCell className="min-w-[180px]">
        <Select
          value={slabForm.calculation_name}
          onValueChange={(value) => onFieldChange("calculation_name", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select calculation" />
          </SelectTrigger>
          <SelectContent>
            {CALCULATION_NAME_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <InlineError message={slabErrors.calculation_name} />
      </TableCell>
      <TableCell className="min-w-[220px]">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Input
              type="number"
              min="0"
              value={slabForm.min_amount}
              onChange={(event) => onFieldChange("min_amount", event.target.value)}
              placeholder="Min"
            />
            <InlineError message={slabErrors.min_amount} />
          </div>
          <div>
            <Input
              type="number"
              min="0"
              value={slabForm.max_amount}
              onChange={(event) => onFieldChange("max_amount", event.target.value)}
              placeholder="Max"
            />
            <InlineError message={slabErrors.max_amount} />
          </div>
        </div>
      </TableCell>
      <TableCell className="min-w-[150px]">
        <RateInput
          value={slabForm.commission_rate}
          calculationName={slabForm.calculation_name}
          onChange={(event) => onFieldChange("commission_rate", event.target.value)}
          error={slabErrors.commission_rate}
        />
      </TableCell>
      <TableCell className="min-w-[130px]">
        <Input
          type="number"
          min="0"
          step="0.01"
          value={slabForm.capping}
          onChange={(event) => onFieldChange("capping", event.target.value)}
          placeholder="Capping"
        />
        <InlineError message={slabErrors.capping} />
      </TableCell>
      <TableCell className="min-w-[150px]">
        <Input
          type="number"
          min="0"
          step="0.01"
          value={slabForm.admin_commission}
          onChange={(event) => onFieldChange("admin_commission", event.target.value)}
          placeholder="Admin"
        />
        <InlineError message={slabErrors.admin_commission} />
      </TableCell>
      <TableCell className="min-w-[160px]">
        <Input
          type="number"
          min="0"
          step="0.01"
          value={slabForm.employee_commission}
          onChange={(event) => onFieldChange("employee_commission", event.target.value)}
          placeholder="Employee"
        />
        <InlineError message={slabErrors.employee_commission} />
      </TableCell>
      <TableCell className="min-w-[140px]">
        <Select value={slabForm.status} onValueChange={(value) => onFieldChange("status", value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            {PACKAGE_STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="min-w-[150px]">
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={onSave}>
            <Check className="mr-1 h-4 w-4" />
            Save
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel}>
            <X className="mr-1 h-4 w-4" />
            Cancel
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Bank</TableHead>
            <TableHead>Loan Type</TableHead>
            <TableHead>Calculation</TableHead>
            <TableHead>Range</TableHead>
            <TableHead>Rate</TableHead>
            <TableHead>Capping</TableHead>
            <TableHead>Admin Commission</TableHead>
            <TableHead>Employee Commission</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {slabs.map((slab) =>
            editingSlabId === slab.id ? (
              renderEditableRow(slab.id)
            ) : (
              <TableRow key={slab.id}>
                <TableCell>{slab.bank}</TableCell>
                <TableCell>{labelForOption(LOAN_TYPE_OPTIONS, slab.loan_type)}</TableCell>
                <TableCell>
                  {labelForOption(CALCULATION_NAME_OPTIONS, slab.calculation_name || "percentage")}
                </TableCell>
                <TableCell>
                  {formatCurrency(slab.min_amount)} - {formatCurrency(slab.max_amount)}
                </TableCell>
                <TableCell>
                  {slab.calculation_name === "flat"
                    ? formatCurrency(slab.commission_rate)
                    : `${Number(slab.commission_rate).toFixed(2)}%`}
                </TableCell>
                <TableCell>{formatCurrency(slab.capping)}</TableCell>
                <TableCell>
                  {slab.calculation_name === "flat"
                    ? formatCurrency(slab.admin_commission)
                    : `${Number(slab.admin_commission).toFixed(2)}%`}
                </TableCell>
                <TableCell>
                  {slab.calculation_name === "flat"
                    ? formatCurrency(slab.employee_commission)
                    : `${Number(slab.employee_commission).toFixed(2)}%`}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={statusBadgeClass(slab.status)}>
                    {labelForOption(PACKAGE_STATUS_OPTIONS, slab.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" disabled={editingSlabId !== null}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(slab)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit Slab
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        className="text-red-600 focus:text-red-600"
                        onClick={() => onDelete(slab)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            )
          )}
          {editingSlabId === "new" ? renderEditableRow("new") : null}
          {!slabs.length && editingSlabId !== "new" ? (
            <TableRow>
              <TableCell colSpan={10} className="py-10 text-center text-sm text-slate-500">
                No slabs added yet.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      {/* <div className="border-t border-slate-200 bg-slate-50/70 px-4 py-3">
        <Button
          className="bg-emerald-600 hover:bg-emerald-700"
          onClick={onAddNew}
          disabled={editingSlabId !== null}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Slab
        </Button>
      </div> */}
    </div>
  );
}

export function CommissionSlabs() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [packageDialogMode, setPackageDialogMode] = useState("create");
  const [packageForm, setPackageForm] = useState(defaultPackageForm);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [deleteState, setDeleteState] = useState(null);
  const userTypeOptions = USER_TYPE_OPTIONS;

  const fetchPackages = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const resp = await getCommissionPackages(token);
      const items = Array.isArray(resp?.data) ? resp.data : [];
      setPackages(items.map(normalizePackage));
    } catch (error) {
      setPackages([]);
      toast.error(error?.message || "Failed to load commission packages");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const summary = useMemo(() => {
    const totalSlabs = packages.reduce((count, item) => count + Number(item.slab_count || item.slabs.length || 0), 0);
    return {
      totalPackages: packages.length,
      activePackages: packages.filter((item) => item.status === "active").length,
      totalSlabs,
    };
  }, [packages]);

  const openCreatePackage = () => {
    setPackageDialogMode("create");
    setPackageForm({
      ...defaultPackageForm,
      user_type: userTypeOptions[0]?.value || "agent",
    });
    setPackageDialogOpen(true);
  };

  const openEditPackage = (pkg) => {
    setSelectedPackageId(pkg.id);
    setPackageDialogMode("edit");
    setPackageForm({
      package_name: pkg.package_name,
      user_type: pkg.user_type,
      status: pkg.status,
    });
    setPackageDialogOpen(true);
  };

  const selectPackage = (pkg) => {
    setSelectedPackageId(pkg.id);
  };

  const openConfigSlabsPage = (pkg) => {
    setSelectedPackageId(pkg.id);
    navigate(`${location.pathname}/${pkg.id}`);
  };

  const savePackage = async () => {
    const trimmedName = packageForm.package_name.trim();
    if (!trimmedName || !token) return;
    try {
      if (packageDialogMode === "create") {
        const resp = await createCommissionPackage(token, {
          package_name: trimmedName,
          user_type: packageForm.user_type,
          status: packageForm.status,
        });
        const nextPackage = normalizePackage(resp?.data || {});
        setPackages((current) => [nextPackage, ...current]);
        setSelectedPackageId(nextPackage.id);
        toast.success("Commission package created");
      } else if (selectedPackageId) {
        await updateCommissionPackage(token, selectedPackageId, {
          package_name: trimmedName,
          user_type: packageForm.user_type,
          status: packageForm.status,
        });
        setPackages((current) =>
          current.map((item) =>
            item.id === selectedPackageId
              ? {
                ...item,
                package_name: trimmedName,
                user_type: packageForm.user_type,
                status: packageForm.status,
              }
              : item
          )
        );
        toast.success("Commission package updated");
      }
      setPackageDialogOpen(false);
    } catch (error) {
      toast.error(error?.message || "Failed to save commission package");
    }
  };

  const confirmDeletePackage = (pkg) => {
    setDeleteState({
      type: "package",
      id: pkg.id,
      title: "Delete package?",
      description:
        "This will remove the package and all slabs configured inside it.",
    });
  };

  const handleDelete = async () => {
    if (!deleteState || !token) return;
    try {
      if (deleteState.type === "package") {
        await deleteCommissionPackage(token, deleteState.id);
        setPackages((current) => current.filter((item) => item.id !== deleteState.id));
        if (selectedPackageId === deleteState.id) {
          setSelectedPackageId(null);
        }
        toast.success("Commission package deleted");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to delete commission package");
    }
    setDeleteState(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            Commission Packages
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Create package basic info first, then manage slabs inside package edit.
          </p>
        </div>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={openCreatePackage}>
          <PackagePlus className="mr-2 h-4 w-4" />
          Add Package
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Packages</CardDescription>
            <CardTitle className="text-2xl">{summary.totalPackages}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active Packages</CardDescription>
            <CardTitle className="text-2xl">{summary.activePackages}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Slabs</CardDescription>
            <CardTitle className="text-2xl">{summary.totalSlabs}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <div className="grid gap-6 ">
        <Card>
          <CardHeader>
            <CardTitle>Commission Package Listing</CardTitle>
            <CardDescription>
              Manage package basics here. Slabs are available only after opening package edit.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="rounded-xl border border-slate-200 px-6 py-10 text-center text-sm text-slate-600">
                Loading commission packages...
              </div>
            ) : packages.length === 0 ? (
              <EmptyState
                title="No commission packages yet"
                description="Create the first package to start configuring user-type-wise commission structures."
                actionLabel="Add Package"
                onAction={openCreatePackage}
              />
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Package Name</TableHead>
                      <TableHead>User Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Total Slabs</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {packages.map((pkg) => (
                      <TableRow
                        key={pkg.id}
                        className={`cursor-pointer ${selectedPackageId === pkg.id ? "bg-emerald-50/60" : ""}`}
                        onClick={() => selectPackage(pkg)}
                      >
                        <TableCell className="font-medium text-slate-900">
                          <button
                            type="button"
                            className="text-left"

                          >
                            {pkg.package_name}
                          </button>
                        </TableCell>
                        <TableCell>
                          {labelForOption(USER_TYPE_OPTIONS, pkg.user_type)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusBadgeClass(pkg.status)}>
                            {labelForOption(PACKAGE_STATUS_OPTIONS, pkg.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>{pkg.slab_count || pkg.slabs.length}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditPackage(pkg)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Package
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openConfigSlabsPage(pkg)}>
                                <PackageCheck className="mr-2 h-4 w-4" />
                                Config Slabs
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={() => confirmDeletePackage(pkg)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

      </div>

      <PackageFormDialog
        open={packageDialogOpen}
        mode={packageDialogMode}
        form={packageForm}
        userTypeOptions={userTypeOptions}
        onFormChange={setPackageForm}
        onClose={setPackageDialogOpen}
        onSubmit={savePackage}
      />

      <DeleteDialog
        open={Boolean(deleteState)}
        title={deleteState?.title || ""}
        description={deleteState?.description || ""}
        onClose={() => setDeleteState(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

export function CommissionPackageConfigPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { packageId } = useParams();
  const { token } = useAuth();
  const [packageItem, setPackageItem] = useState(null);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [packageForm, setPackageForm] = useState(defaultPackageForm);
  const [editingSlabId, setEditingSlabId] = useState(null);
  const [slabForm, setSlabForm] = useState(defaultSlabForm);
  const [slabErrors, setSlabErrors] = useState({});
  const [deleteState, setDeleteState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bankOptions, setBankOptions] = useState(DEFAULT_BANK_OPTIONS);
  const [bankCatalog, setBankCatalog] = useState([]);

  const fetchPackageDetail = useCallback(async () => {
    if (!token || !packageId) return;
    setLoading(true);
    try {
      const resp = await getCommissionPackage(token, packageId);
      setPackageItem(normalizePackage(resp?.data || {}));
    } catch (error) {
      setPackageItem(null);
      toast.error(error?.message || "Failed to load commission package");
    } finally {
      setLoading(false);
    }
  }, [token, packageId]);

  useEffect(() => {
    fetchPackageDetail();
  }, [fetchPackageDetail]);

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try {
        const resp = await getBanks(token);
        const items = Array.isArray(resp?.data) ? resp.data : [];
        if (!active || items.length === 0) return;
        setBankCatalog(items);
        setBankOptions(["All Banks", ...items.map((item) => item.name)]);
      } catch {
        if (active) {
          setBankCatalog([]);
          setBankOptions(DEFAULT_BANK_OPTIONS);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!packageItem) return;
    setPackageForm({
      package_name: packageItem.package_name,
      user_type: packageItem.user_type,
      status: packageItem.status,
    });
  }, [packageItem]);

  const listPath = location.pathname.replace(/\/[^/]+$/, "");

  const savePackage = async () => {
    if (!packageId || !token) return;
    const trimmedName = packageForm.package_name.trim();
    if (!trimmedName) return;
    try {
      await updateCommissionPackage(token, packageId, {
        package_name: trimmedName,
        user_type: packageForm.user_type,
        status: packageForm.status,
      });
      setPackageItem((current) =>
        current
          ? {
            ...current,
            package_name: trimmedName,
            user_type: packageForm.user_type,
            status: packageForm.status,
          }
          : current
      );
      setPackageDialogOpen(false);
      toast.success("Commission package updated");
    } catch (error) {
      toast.error(error?.message || "Failed to update commission package");
    }
  };

  const openCreateSlab = () => {
    setSlabForm(defaultSlabForm);
    setSlabErrors({});
    setEditingSlabId("new");
  };

  const openEditSlab = (slab) => {
    setEditingSlabId(slab.id);
    setSlabForm({
      loan_type: slab.loan_type,
      bank: slab.bank,
      calculation_name: slab.calculation_name || "percentage",
      capping: String(slab.capping ?? ""),
      admin_commission: String(slab.admin_commission ?? ""),
      employee_commission: String(slab.employee_commission ?? ""),
      min_amount: String(slab.min_amount),
      max_amount: String(slab.max_amount),
      commission_rate: String(slab.commission_rate),
      status: slab.status,
    });
    setSlabErrors({});
  };

  const updateSlabField = (field, value) => {
    setSlabForm((current) => ({ ...current, [field]: value }));
    setSlabErrors((current) => ({ ...current, [field]: undefined }));
  };

  const cancelInlineSlab = () => {
    setEditingSlabId(null);
    setSlabForm(defaultSlabForm);
    setSlabErrors({});
  };

  const saveSlab = async () => {
    if (!packageId || !token) return;
    const nextErrors = validateSlabForm(slabForm);
    if (Object.keys(nextErrors).length) {
      setSlabErrors(nextErrors);
      return;
    }
    const selectedBank = slabForm.bank === "All Banks"
      ? null
      : bankCatalog.find((item) => item.name === slabForm.bank);
    const payload = {
      loan_type: slabForm.loan_type,
      bank_id: selectedBank?.id || null,
      bank_name: slabForm.bank,
      calculation_name: slabForm.calculation_name,
      capping: Number(slabForm.capping || 0),
      admin_commission: Number(slabForm.admin_commission || 0),
      employee_commission: Number(slabForm.employee_commission || 0),
      min_amount: Number(slabForm.min_amount || 0),
      max_amount: Number(slabForm.max_amount || 0),
      rate: Number(slabForm.commission_rate || 0),
      status: slabForm.status,
    };
    try {
      if (editingSlabId === "new") {
        await createCommissionSlab(token, packageId, payload);
        toast.success("Commission slab created");
      } else {
        await updateCommissionSlab(token, packageId, editingSlabId, payload);
        toast.success("Commission slab updated");
      }
      await fetchPackageDetail();
      cancelInlineSlab();
    } catch (error) {
      toast.error(error?.message || "Failed to save commission slab");
    }
  };

  const toggleSlabStatus = async (slabId) => {
    if (!packageId || !token || !packageItem) return;
    const slab = packageItem.slabs.find((item) => item.id === slabId);
    if (!slab) return;
    try {
      await updateCommissionSlab(token, packageId, slabId, {
        status: slab.status === "active" ? "inactive" : "active",
      });
      await fetchPackageDetail();
    } catch (error) {
      toast.error(error?.message || "Failed to update slab status");
    }
  };

  const handleDelete = async () => {
    if (!deleteState || !packageId || !token) return;
    try {
      await deleteCommissionSlab(token, packageId, deleteState.id);
      await fetchPackageDetail();
      toast.success("Commission slab deleted");
    } catch (error) {
      toast.error(error?.message || "Failed to delete commission slab");
    } finally {
      setDeleteState(null);
    }
  };

  if (loading && !packageItem) {
    return (
      <Card>
        <CardContent className="space-y-4 p-8">
          <Button variant="outline" className="w-fit" onClick={() => navigate(listPath)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Packages
          </Button>
          <div className="rounded-xl border border-slate-200 px-6 py-10 text-center text-sm text-slate-600">
            Loading commission package...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!packageItem && !loading) {
    return (
      <Card>
        <CardContent className="space-y-4 p-8">
          <Button variant="outline" className="w-fit" onClick={() => navigate(listPath)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Packages
          </Button>
          <EmptyState
            title="Package not found"
            description="This package may have been deleted or is no longer available."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <Button variant="outline" className="w-fit" onClick={() => navigate(listPath)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Packages
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">
              {packageItem.package_name}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Package-linked slab configuration page. All slab CRUD stays scoped to this package.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={statusBadgeClass(packageItem.status)}>
            {labelForOption(PACKAGE_STATUS_OPTIONS, packageItem.status)}
          </Badge>
          <Button variant="outline" onClick={() => setPackageDialogOpen(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Package
          </Button>

        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Package Details</CardTitle>
          <CardDescription>Basic package information with connected slab management.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-4">
          <div>
            <p className="text-sm text-slate-500">Package Name</p>
            <p className="font-semibold text-slate-900">{packageItem.package_name}</p>
          </div>
          <div>
            <p className="text-sm text-slate-500">User Type</p>
            <p className="font-semibold text-slate-900">
              {labelForOption(USER_TYPE_OPTIONS, packageItem.user_type)}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Status</p>
            <p className="font-semibold text-slate-900">
              {labelForOption(PACKAGE_STATUS_OPTIONS, packageItem.status)}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500">Total Slabs</p>
            <p className="font-semibold text-slate-900">{packageItem.slab_count || packageItem.slabs.length}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between">
            <div className="flex flex-col gap-2">
              <CardTitle>Slab Management</CardTitle>
              <CardDescription>Add, edit, delete, or toggle slabs for this package only.</CardDescription>

            </div>
            <div className=" border-slate-200 bg-slate-50/70">
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={openCreateSlab}
                disabled={editingSlabId !== null}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Slab
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <InlineSlabTable
            slabs={packageItem?.slabs || []}
            bankOptions={bankOptions}
            editingSlabId={editingSlabId}
            slabForm={slabForm}
            slabErrors={slabErrors}
            onFieldChange={updateSlabField}
            onAddNew={openCreateSlab}
            onEdit={openEditSlab}
            onSave={saveSlab}
            onCancel={cancelInlineSlab}
            onDelete={(slab) =>
              setDeleteState({
                id: slab.id,
                title: "Delete slab?",
                description: "This slab will be removed from the selected package.",
              })
            }
            onToggleStatus={toggleSlabStatus}
          />
        </CardContent>
      </Card>

      <PackageFormDialog
        open={packageDialogOpen}
        mode="edit"
        form={packageForm}
        userTypeOptions={USER_TYPE_OPTIONS}
        onFormChange={setPackageForm}
        onClose={setPackageDialogOpen}
        onSubmit={savePackage}
      />

      <DeleteDialog
        open={Boolean(deleteState)}
        title={deleteState?.title || ""}
        description={deleteState?.description || ""}
        onClose={() => setDeleteState(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}

const COMMISSION_RECORD_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "paid", label: "Paid" },
];

function commissionRecordBadgeClass(status) {
  return status === "paid"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === "approved"
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : "bg-amber-50 text-amber-700 border-amber-200";
}

function buildPaginationItems(page, totalPages) {
  const pages = [];
  for (let current = 1; current <= totalPages; current += 1) {
    if (
      current === 1 ||
      current === totalPages ||
      Math.abs(current - page) <= 1
    ) {
      pages.push(current);
    } else if (pages[pages.length - 1] !== "...") {
      pages.push("...");
    }
  }
  return pages;
}

function RecordsPagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const items = buildPaginationItems(page, totalPages);
  return (
    <Pagination className="justify-end">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page > 1) onPageChange(page - 1);
            }}
            className={page <= 1 ? "pointer-events-none opacity-50" : ""}
          />
        </PaginationItem>
        {items.map((item, index) => (
          <PaginationItem key={`${item}-${index}`}>
            {item === "..." ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href="#"
                isActive={item === page}
                onClick={(event) => {
                  event.preventDefault();
                  onPageChange(item);
                }}
              >
                {item}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page < totalPages) onPageChange(page + 1);
            }}
            className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

function CommissionRecordViewDialog({ open, record, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Commission Record Details</DialogTitle>
          <DialogDescription>View complete payout and commission information for this record.</DialogDescription>
        </DialogHeader>
        {record ? (
          <div className="grid gap-4 py-2 md:grid-cols-2">
            <div>
              <p className="text-sm text-slate-500">Recipient</p>
              <p className="font-medium text-slate-900">{record.agentName}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Role</p>
              <p className="font-medium text-slate-900">{labelForOption(USER_TYPE_OPTIONS, record.recipientRole)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Loan ID</p>
              <p className="font-mono text-sm text-slate-900">{record.loanId}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Package</p>
              <p className="font-medium text-slate-900">{record.packageName}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Bank</p>
              <p className="font-medium text-slate-900">{record.bank}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Loan Type</p>
              <p className="font-medium text-slate-900">{labelForOption(LOAN_TYPE_OPTIONS, record.loanType)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Calculation</p>
              <p className="font-medium text-slate-900">{labelForOption(CALCULATION_NAME_OPTIONS, record.calculationName)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Applied Rate</p>
              <p className="font-medium text-slate-900">
                {record.calculationName === "flat" ? formatCurrency(record.appliedRate) : `${record.appliedRate}%`}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Disbursed Amount</p>
              <p className="font-medium text-slate-900">{formatCurrency(record.disbursedAmount)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Capping</p>
              <p className="font-medium text-slate-900">{formatCurrency(record.capping)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Commission Amount</p>
              <p className="font-medium text-slate-900">{formatCurrency(record.amount)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Status</p>
              <Badge variant="outline" className={commissionRecordBadgeClass(record.status)}>
                {labelForOption(COMMISSION_RECORD_STATUS_OPTIONS, record.status)}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-slate-500">Created</p>
              <p className="font-medium text-slate-900">{formatDateTime(record.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Paid Date</p>
              <p className="font-medium text-slate-900">{record.paidAt ? formatDateTime(record.paidAt) : "—"}</p>
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onClose(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CommissionRecordEditDialog({ open, form, onFormChange, onClose, onSubmit, saving }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Edit Commission Record</DialogTitle>
          <DialogDescription>Update commission status or amount for this entry.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="commission_amount">Commission Amount</Label>
            <Input
              id="commission_amount"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(event) => onFormChange((current) => ({ ...current, amount: event.target.value }))}
            />
          </div>
          <div className="grid gap-2">
            <Label>Status</Label>
            <Select
              value={form.status}
              onValueChange={(value) => onFormChange((current) => ({ ...current, status: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {COMMISSION_RECORD_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={onSubmit}
            disabled={saving}
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useCommissionRecordsList(params = {}) {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  });

  const queryKey = JSON.stringify(params);
  const requestParams = useMemo(() => JSON.parse(queryKey || "{}"), [queryKey]);

  const fetchRows = useCallback(async (nextPage = 1) => {
    if (!token) return;
    setLoading(true);
    try {
      const resp = await getCommissionRecords(token, {
        ...requestParams,
        page: nextPage,
        pageSize: 10,
      });
      const items = resp?.data?.items ?? [];
      const meta = resp?.data?.pagination || {};
      setRows(items.map(normalizeCommissionRecord));
      setPagination({
        page: Number(meta.page || nextPage),
        pageSize: Number(meta.page_size || 10),
        total: Number(meta.total || items.length || 0),
        totalPages: Number(meta.total_pages || 1),
      });
      setPage(Number(meta.page || nextPage));
    } catch (error) {
      setRows([]);
      setPagination({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
      });
      toast.error(error?.message || "Failed to load commission ledger");
    } finally {
      setLoading(false);
    }
  }, [token, requestParams]);

  useEffect(() => {
    setPage(1);
  }, [queryKey]);

  useEffect(() => {
    fetchRows(page);
  }, [fetchRows, page]);

  return {
    rows,
    loading,
    page,
    pagination,
    setPage,
    refresh: () => fetchRows(page),
  };
}

function CommissionRecordsCard({
  title,
  description,
  params = {},
  mode = "ledger",
  emptyMessage,
  enableFilters = false,
}) {
  const { token, user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const effectiveParams = useMemo(() => {
    const nextParams = { ...params };
    if (enableFilters) {
      const trimmed = searchTerm.trim();
      if (trimmed) nextParams.q = trimmed;
      if (statusFilter !== "all") nextParams.status = statusFilter;
    }
    return nextParams;
  }, [params, enableFilters, searchTerm, statusFilter]);
  const { rows, loading, page, pagination, setPage, refresh } = useCommissionRecordsList(effectiveParams);
  const [viewState, setViewState] = useState({ open: false, record: null });
  const [editState, setEditState] = useState({ open: false, record: null });
  const [editForm, setEditForm] = useState({ amount: "", status: "pending" });
  const [deleteState, setDeleteState] = useState(null);
  const [saving, setSaving] = useState(false);
  const canManage = ["super_admin", "wl_admin"].includes(user?.role);

  const loadRecordDetail = useCallback(async (recordId) => {
    if (!token) return null;
    const resp = await getCommissionRecord(token, recordId);
    return normalizeCommissionRecord(resp?.data || {});
  }, [token]);

  const openView = async (recordId) => {
    try {
      const record = await loadRecordDetail(recordId);
      if (!record) return;
      setViewState({ open: true, record });
    } catch (error) {
      toast.error(error?.message || "Failed to load commission record");
    }
  };

  const openEdit = async (recordId) => {
    try {
      const record = await loadRecordDetail(recordId);
      if (!record) return;
      setEditState({ open: true, record });
      setEditForm({
        amount: String(record.amount ?? ""),
        status: record.status || "pending",
      });
    } catch (error) {
      toast.error(error?.message || "Failed to load commission record");
    }
  };

  const submitEdit = async () => {
    if (!editState.record || !token) return;
    setSaving(true);
    try {
      await updateCommissionRecord(token, editState.record.id, {
        amount: Number(editForm.amount || 0),
        status: editForm.status,
      });
      toast.success("Commission record updated");
      setEditState({ open: false, record: null });
      await refresh();
    } catch (error) {
      toast.error(error?.message || "Failed to update commission record");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteState?.id || !token) return;
    setSaving(true);
    try {
      await deleteCommissionRecord(token, deleteState.id);
      toast.success("Commission record deleted");
      setDeleteState(null);
      await refresh();
    } catch (error) {
      toast.error(error?.message || "Failed to delete commission record");
    } finally {
      setSaving(false);
    }
  };

  const renderStatus = (row) => {
    if (mode === "approved") {
      return (
        <Badge
          variant="outline"
          className={row.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200"}
        >
          {row.status === "paid" ? "Paid" : "Approved"}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className={commissionRecordBadgeClass(row.status)}>
        {labelForOption(COMMISSION_RECORD_STATUS_OPTIONS, row.status)}
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {enableFilters ? (
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px_auto]">
            <div className="grid gap-2">
              <Label htmlFor="commission-ledger-search">Search</Label>
              <Input
                id="commission-ledger-search"
                placeholder="Search recipient, loan ID, bank, package, loan type"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {COMMISSION_RECORD_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        ) : null}

        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                {mode === "ledger" ? <TableHead>Loan ID</TableHead> : null}
                {mode === "ledger" ? <TableHead>Bank</TableHead> : null}
                <TableHead>Amount</TableHead>
                <TableHead>{mode === "approved" ? "Processed Date" : "Request Date"}</TableHead>
                <TableHead>Status</TableHead>
                {canManage ? <TableHead className="text-right">Actions</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canManage ? (mode === "ledger" ? 7 : 5) : (mode === "ledger" ? 6 : 4)} className="text-center text-slate-500">
                    Loading commission records...
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? (mode === "ledger" ? 7 : 5) : (mode === "ledger" ? 6 : 4)} className="text-center text-slate-500">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.agentName}</TableCell>
                  {mode === "ledger" ? <TableCell>{row.loanId}</TableCell> : null}
                  {mode === "ledger" ? <TableCell>{row.bank}</TableCell> : null}
                  <TableCell>{formatCurrency(row.amount)}</TableCell>
                  <TableCell>{mode === "approved" ? (row.processedDate || "In progress") : row.requestDate}</TableCell>
                  <TableCell>{renderStatus(row)}</TableCell>
                  {canManage ? (
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openView(row.id)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEdit(row.id)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-600"
                            onClick={() =>
                              setDeleteState({
                                id: row.id,
                                title: "Delete commission record?",
                                description: "This will permanently remove the selected commission entry from the ledger.",
                              })
                            }
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500">
            Showing page {pagination.page} of {pagination.totalPages} · {pagination.total} records
          </p>
          <RecordsPagination page={page} totalPages={pagination.totalPages} onPageChange={setPage} />
        </div>
      </CardContent>

      <CommissionRecordViewDialog
        open={viewState.open}
        record={viewState.record}
        onClose={(next) => setViewState({ open: Boolean(next), record: next ? viewState.record : null })}
      />

      <CommissionRecordEditDialog
        open={editState.open}
        form={editForm}
        onFormChange={setEditForm}
        onClose={(next) => setEditState({ open: Boolean(next), record: next ? editState.record : null })}
        onSubmit={submitEdit}
        saving={saving}
      />

      <DeleteDialog
        open={Boolean(deleteState)}
        title={deleteState?.title || ""}
        description={deleteState?.description || ""}
        onClose={() => setDeleteState(null)}
        onConfirm={confirmDelete}
      />
    </Card>
  );
}

export function PendingPayouts() {
  return (
    <CommissionRecordsCard
      title="Pending Payouts"
      description="Pending commission entries generated after loan disbursement."
      params={{ status: "pending" }}
      mode="pending"
      emptyMessage="No pending payout records found."
    />
  );
}

export function ApprovedPayouts() {
  return (
    <CommissionRecordsCard
      title="Approved Payouts"
      description="Approved commission entries from the backend ledger."
      params={{ status: "approved" }}
      mode="approved"
      emptyMessage="No approved payout records found."
    />
  );
}

export function CommissionLedger() {
  return (
    <CommissionRecordsCard
      title="Commission Ledger"
      description="Database-backed commission history generated on disbursement."
      params={{}}
      mode="ledger"
      emptyMessage="No commission ledger records found."
      enableFilters
    />
  );
}

export default CommissionSlabs;
